import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  TimePicker,
  normalizeTimeValue,
} from '@roger/ui-components';
import {
  createMarMedication,
  updateMarMedication,
} from '../../../services/api';
import type {
  MarMedicationResponse,
  PatientMedicationResponse,
} from '../../../services/api';
import { useToast } from '../../../hooks/useToastContext';
import { getMedicationDisplayName } from '../medicationUtils';

interface MARRecordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  visitId: string;
  /** The profile medication being administered (for create mode and display). */
  medication: PatientMedicationResponse | null;
  /** When set, the dialog edits this existing administration record. */
  existing?: MarMedicationResponse | null;
  onSaved: () => void;
}

const MARRecordDialog: React.FC<MARRecordDialogProps> = ({
  open,
  onOpenChange,
  patientId,
  visitId,
  medication,
  existing,
  onSaved,
}) => {
  const { error: showError } = useToast();
  const [timeAdministered, setTimeAdministered] = useState('');
  const [prnReason, setPrnReason] = useState('');
  const [patientResponse, setPatientResponse] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Sync form state whenever the dialog opens for a new target.
  useEffect(() => {
    if (!open) return;
    setTimeAdministered(normalizeTimeValue(existing?.time_administered));
    setPrnReason(existing?.prn_reason ?? '');
    setPatientResponse(existing?.patient_response ?? '');
  }, [open, existing]);

  const isEdit = Boolean(existing);
  const medForDisplay = existing?.patient_medication ?? medication;

  // An administration record is only meaningful with a time, so require it
  // before allowing a save (prevents recording an empty entry).
  const canSave = Boolean(timeAdministered) && (isEdit || Boolean(medication));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // `canSave` guarantees a non-empty time before this runs; time is required.
      const body = {
        time_administered: timeAdministered,
        prn_reason: prnReason.trim() || null,
        patient_response: patientResponse.trim() || null,
      };

      if (existing) {
        const response = await updateMarMedication({
          path: {
            patient_id: patientId,
            visit_id: visitId,
            mar_id: existing.id,
          },
          body,
        });
        if (response.error) throw new Error('Failed to update record');
      } else {
        if (!medication) return;
        const response = await createMarMedication({
          path: { patient_id: patientId, visit_id: visitId },
          body: { ...body, patient_medication_id: medication.id },
        });
        if (response.error) throw new Error('Failed to create record');
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to save administration record:', err);
      showError('Failed to save administration record. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => !isSaving && onOpenChange(o)}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? 'Edit Administration Record'
              : 'Add Administration Record'}
          </DialogTitle>
          <DialogDescription>
            {medForDisplay
              ? `Record the administration of ${getMedicationDisplayName(medForDisplay)} during this visit.`
              : 'Record the administration details for this visit.'}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          <div>
            <Label
              htmlFor='mar-time'
              className='text-xs text-muted-foreground mb-1 block'
            >
              Time Administered <span className='text-destructive'>*</span>
            </Label>
            <TimePicker
              id='mar-time'
              time={timeAdministered || undefined}
              onTimeChange={time => setTimeAdministered(time ?? '')}
              timePickerSize='default'
              className='w-40'
            />
          </div>

          <div>
            <Label
              htmlFor='mar-prn'
              className='text-xs text-muted-foreground mb-1 block'
            >
              PRN Reason
            </Label>
            <Input
              id='mar-prn'
              value={prnReason}
              onChange={e => setPrnReason(e.target.value)}
              placeholder='Reason given (for PRN medications)'
              inputSize='default'
            />
          </div>

          <div>
            <Label
              htmlFor='mar-response'
              className='text-xs text-muted-foreground mb-1 block'
            >
              Patient Response
            </Label>
            <Textarea
              id='mar-response'
              value={patientResponse}
              onChange={e => setPatientResponse(e.target.value)}
              placeholder="Patient's response to the medication"
              textareaSize='default'
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !canSave}>
            {isSaving && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
            {isEdit ? 'Save Changes' : 'Add Record'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MARRecordDialog;
